import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor, registerPlugin } from '@capacitor/core';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useI18n } from '../context/I18nContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Play, Square, Satellite, AlertTriangle, Mountain } from 'lucide-react';
import { trackDistance, paceSeconds, fmtClock, smoothSpeed, displaySpeed, computeSplits, elevationGain } from '../lib/geo';

// The native plugin. registerPlugin returns a web proxy in the browser that only throws when a
// method is CALLED, so importing this is safe everywhere; we just never call it off-device.
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
const isNative = Capacitor.isNativePlatform();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROUTE_COLOR = '#D96C48';

const L_ = {
  es: {
    back: 'Volver a Entrenamiento', title: 'Grabar carrera',
    webOnly: 'El GPS solo funciona dentro de la app de Android. Aquí ves la pantalla, pero no registra.',
    start: 'Iniciar', stop: 'Finalizar', newRun: 'Nueva carrera',
    distance: 'Distancia', time: 'Tiempo', pace: 'Ritmo', speed: 'Velocidad', avgPace: 'Ritmo medio',
    elevation: 'Desnivel', paceUnit: '/km', kmh: 'km/h', km: 'km', m: 'm',
    signal: 'Señal GPS', points: 'puntos', acc: 'precisión', waiting: 'Buscando GPS…',
    summary: 'Carrera terminada', splits: 'Parciales', partial: 'parcial',
    saveNote: 'El guardado automático en tu sesión de entrenamiento llega en la siguiente fase.',
    savingRun: 'Guardando en tu sesión…', savedToSession: 'Guardado en tu sesión de entrenamiento',
    savedMain: 'Guardado como carrera principal del día',
    savedExtra: 'Guardado como carrera extra del día',
    saveFailed: 'No se pudo guardar en la sesión. Puedes anotarlo a mano en el plan.',
    backToPlan: 'Volver al plan',
    bgTitle: 'MealPlanner · carrera en curso',
    bgMsg: 'Registrando tu carrera con GPS. Toca para volver a la app.',
    permDenied: 'Permiso de ubicación denegado. Actívalo en los ajustes del móvil para grabar.',
    openSettings: 'Abrir ajustes',
    keepScreen: 'Puedes bloquear el móvil: la carrera sigue grabándose en segundo plano.',
  },
  en: {
    back: 'Back to Training', title: 'Record run',
    webOnly: 'GPS only works inside the Android app. Here you can see the screen, but it does not record.',
    start: 'Start', stop: 'Finish', newRun: 'New run',
    distance: 'Distance', time: 'Time', pace: 'Pace', speed: 'Speed', avgPace: 'Average pace',
    elevation: 'Elevation', paceUnit: '/km', kmh: 'km/h', km: 'km', m: 'm',
    signal: 'GPS signal', points: 'points', acc: 'accuracy', waiting: 'Acquiring GPS…',
    summary: 'Run finished', splits: 'Splits', partial: 'partial',
    saveNote: 'Auto-saving into your training session comes in the next phase.',
    savingRun: 'Saving to your session…', savedToSession: 'Saved to your training session',
    savedMain: 'Saved as the main run of the day',
    savedExtra: 'Saved as an extra run of the day',
    saveFailed: "Couldn't save to the session. You can enter it by hand in the plan.",
    backToPlan: 'Back to plan',
    bgTitle: 'MealPlanner · run in progress',
    bgMsg: 'Recording your run with GPS. Tap to return to the app.',
    permDenied: 'Location permission denied. Enable it in your phone settings to record.',
    openSettings: 'Open settings',
    keepScreen: 'You can lock the phone: the run keeps recording in the background.',
  },
};

const fmtDist = (m, t) => (m >= 1000 ? `${(m / 1000).toFixed(2)} ${t.km}` : `${Math.round(m)} ${t.m}`);

function Stat({ label, value, sub }) {
  return (
    <div className="text-center">
      <p className="text-[11px] uppercase tracking-wide text-stone font-inter">{label}</p>
      <p className="font-manrope font-extrabold text-3xl text-charcoal mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-stone font-inter">{sub}</p>}
    </div>
  );
}

export default function RunTrackerPage() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const t = L_[language] || L_.es;

  const [tracking, setTracking] = useState(false);
  const [points, setPoints] = useState([]);
  const [startMs, setStartMs] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [lastAcc, setLastAcc] = useState(null);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saveState, setSaveState] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [saveKind, setSaveKind] = useState(null);   // 'main' | 'extra' — how it was stored
  const watcherRef = useRef(null);
  const rawSpeedRef = useRef(null);
  const lastFixRef = useRef(null);

  // When opened from a running session ( /track?plan=..&session=.. ), the finished run is
  // written back into that session's cardio log.
  const [sp] = useSearchParams();
  const planId = sp.get('plan');
  const sessionId = sp.get('session');

  // Map
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const polyRef = useRef(null);
  const endDotRef = useRef(null);
  const startDotRef = useRef(null);

  const shownPoints = summary ? summary.points : points;
  const showMap = tracking || !!summary;

  // Elapsed + live speed both come off a 1 s tick. Elapsed uses the wall clock (correct even
  // when the screen is locked and callbacks pause); speed drops to 0 when no fresh fix arrives.
  useEffect(() => {
    if (!tracking || !startMs) return;
    const iv = setInterval(() => {
      setElapsed((Date.now() - startMs) / 1000);
      setSpeed(displaySpeed(lastFixRef.current, Date.now(), rawSpeedRef.current));
    }, 1000);
    return () => clearInterval(iv);
  }, [tracking, startMs]);

  // Create the map once its container is on screen; tear it down on unmount.
  useEffect(() => {
    if (!showMap || mapRef.current || !mapElRef.current) return;
    const map = L.map(mapElRef.current, { attributionControl: false }).setView([40.4168, -3.7038], 15);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 120); // container may still be resizing on first paint
  }, [showMap]);
  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  // Draw / update the route as points arrive.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const latlngs = shownPoints.map(p => [p.lat, p.lng]);
    if (!latlngs.length) return;
    if (polyRef.current) polyRef.current.setLatLngs(latlngs);
    else polyRef.current = L.polyline(latlngs, { color: ROUTE_COLOR, weight: 4 }).addTo(map);
    const start = latlngs[0], end = latlngs[latlngs.length - 1];
    if (!startDotRef.current) startDotRef.current = L.circleMarker(start, { radius: 6, color: '#fff', weight: 2, fillColor: '#6B8E6B', fillOpacity: 1 }).addTo(map);
    if (endDotRef.current) endDotRef.current.setLatLng(end);
    else endDotRef.current = L.circleMarker(end, { radius: 6, color: '#fff', weight: 2, fillColor: ROUTE_COLOR, fillOpacity: 1 }).addTo(map);
    map.fitBounds(polyRef.current.getBounds(), { padding: [24, 24], maxZoom: 17 });
  }, [shownPoints]);

  const start = async () => {
    setError(null); setSummary(null); setPoints([]); setSpeed(0);
    rawSpeedRef.current = null; lastFixRef.current = null;
    if (polyRef.current) { polyRef.current.remove(); polyRef.current = null; }
    if (endDotRef.current) { endDotRef.current.remove(); endDotRef.current = null; }
    if (startDotRef.current) { startDotRef.current.remove(); startDotRef.current = null; }
    const t0 = Date.now();
    setStartMs(t0); setElapsed(0); setTracking(true);
    try {
      const id = await BackgroundGeolocation.addWatcher(
        { backgroundMessage: t.bgMsg, backgroundTitle: t.bgTitle, requestPermissions: true, stale: false, distanceFilter: 5 },
        (location, err) => {
          if (err) {
            setError(err.code === 'NOT_AUTHORIZED' ? t.permDenied : (err.message || String(err)));
            return;
          }
          setPoints(prev => [...prev, {
            lat: location.latitude, lng: location.longitude, alt: location.altitude,
            t: location.time ?? Date.now(), speed: location.speed, accuracy: location.accuracy,
          }]);
          lastFixRef.current = location.time ?? Date.now();
          if (location.accuracy != null) setLastAcc(location.accuracy);
          if (location.speed != null && location.speed >= 0) {
            rawSpeedRef.current = smoothSpeed(rawSpeedRef.current, location.speed);
          }
        }
      );
      watcherRef.current = id;
    } catch (e) {
      setError(e.message || String(e));
      setTracking(false);
    }
  };

  // Write the finished run into the plan's cardio session. Replaces the whole session (the API's
  // shape), so we fetch it, patch its cardio, and put it back. The FIRST run of the day is the
  // main one (the prescribed exercise → cardio.log); any further runs the same day are stored as
  // extras (cardio.extra_runs) so the record keeps them all instead of overwriting.
  const saveToSession = async (run) => {
    if (!planId || !sessionId) return;
    setSaveState('saving');
    try {
      const res = await fetch(`${API}/training/plans/${planId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('plan');
      const plan = await res.json();
      let target = null;
      for (const w of plan.week_list || []) for (const s of (w.sessions || [])) if (s.session_id === sessionId) target = s;
      if (!target) throw new Error('session');
      const cur = target.cardio || { type: 'carrera' };
      const hasMain = cur.log && cur.log.distance_m != null && cur.log.distance_m !== '';
      const entry = {
        distance_m: Math.round(run.distance),
        time_sec: Math.round(run.time),
        elevation_m: Math.round(run.elevation || 0),
        pace_sec: run.pace ? Math.round(run.pace) : null,
        recorded_at: new Date().toISOString(),
      };
      const cardio = hasMain
        ? { ...cur, extra_runs: [...(cur.extra_runs || []), entry] }
        : { ...cur, log: { ...(cur.log || {}), distance_m: entry.distance_m, time_sec: entry.time_sec, elevation_m: entry.elevation_m } };
      const put = await fetch(`${API}/training/plans/${planId}/sessions/${sessionId}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...target, cardio }),
      });
      if (!put.ok) { setSaveState('error'); return; }
      setSaveKind(hasMain ? 'extra' : 'main');
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
    }
  };

  const stop = async () => {
    try { if (watcherRef.current) await BackgroundGeolocation.removeWatcher({ id: watcherRef.current }); }
    catch (e) { /* stopping is best-effort */ }
    watcherRef.current = null;
    const dur = startMs ? (Date.now() - startMs) / 1000 : elapsed;
    const dist = trackDistance(points);
    const elev = elevationGain(points);
    const pc = paceSeconds(dur, dist);
    setSummary({
      points: [...points], distance: dist, time: dur, pace: pc,
      splits: computeSplits(points), elevation: elev,
    });
    setTracking(false); setSpeed(0);
    if (planId && sessionId && dist > 0) saveToSession({ distance: dist, time: dur, elevation: elev, pace: pc });
  };

  const reset = () => {
    setSummary(null); setPoints([]); setElapsed(0); setStartMs(null); setSaveState(null); setSaveKind(null);
    // Tear the map down so the next run builds a fresh one. Without this, mapRef still pointed at
    // the now-unmounted container, so "Nueva carrera" showed no map (the create effect saw a
    // non-null mapRef and bailed out).
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    polyRef.current = null; endDotRef.current = null; startDotRef.current = null;
  };

  const distance = summary ? summary.distance : trackDistance(points);
  const dispTime = summary ? summary.time : elapsed;
  const pace = summary ? summary.pace : paceSeconds(elapsed, distance);
  const elevation = summary ? summary.elevation : elevationGain(points);
  const speedKmh = speed > 0 ? speed * 3.6 : null;

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-6">
        <button onClick={() => navigate(planId ? `/training/plans/${planId}` : '/training')} className="flex items-center gap-2 text-stone hover:text-primary font-inter text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> {planId ? t.backToPlan : t.back}
        </button>
        <h1 className="font-manrope font-bold text-2xl text-charcoal mb-4 flex items-center gap-2">
          <Satellite className="w-6 h-6 text-primary" /> {t.title}
        </h1>

        {!isNative && !summary && (
          <div className="flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 p-3 mb-4 text-sm font-inter">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{t.webOnly}</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-200 text-red-700 p-3 mb-4 text-sm font-inter">
            {error}
            {isNative && (
              <Button variant="outline" size="sm" onClick={() => BackgroundGeolocation.openSettings?.()} className="mt-2 rounded-full h-9">
                {t.openSettings}
              </Button>
            )}
          </div>
        )}

        {/* Map */}
        {showMap && (
          <div ref={mapElRef} className="w-full h-64 rounded-2xl overflow-hidden border border-border mb-4 z-0" data-testid="run-map" />
        )}

        <Card className="bg-white rounded-3xl shadow-soft p-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <Stat label={t.distance} value={fmtDist(distance, t)} />
            <Stat label={t.time} value={fmtClock(dispTime)} />
            <Stat label={summary ? t.avgPace : t.pace} value={pace ? fmtClock(pace) : '—'} sub={pace ? t.paceUnit : undefined} />
            {summary
              ? <Stat label={t.elevation} value={`${Math.round(elevation)} ${t.m}`} />
              : <Stat label={t.speed} value={speedKmh != null ? speedKmh.toFixed(1) : '—'} sub={speedKmh != null ? t.kmh : undefined} />}
          </div>

          {tracking && (
            <div className="mt-5 pt-4 border-t border-border text-center">
              <p className="text-xs text-stone font-inter flex items-center justify-center gap-3">
                <span>{t.signal}: {points.length > 0
                  ? <><b>{points.length}</b>{lastAcc != null ? <> · ±{Math.round(lastAcc)} m</> : null}</>
                  : t.waiting}</span>
                <span className="flex items-center gap-1"><Mountain className="w-3.5 h-3.5" /> {Math.round(elevation)} {t.m}</span>
              </p>
              <p className="text-[11px] text-sage font-inter mt-1">{t.keepScreen}</p>
            </div>
          )}
        </Card>

        {/* Splits table (details view) */}
        {summary && summary.splits.length > 0 && (
          <Card className="bg-white rounded-3xl shadow-soft p-5 mt-4">
            <p className="font-manrope font-bold text-charcoal mb-2">{t.splits}</p>
            <div className="space-y-1">
              {summary.splits.map((s) => (
                <div key={s.index} className="flex items-center gap-3 text-sm font-inter">
                  <span className="w-6 text-stone tabular-nums">{s.index}</span>
                  <span className="w-20 text-charcoal tabular-nums">
                    {s.partial ? `${s.distanceM} ${t.m}` : `${s.index} ${t.km}`}
                  </span>
                  <div className="flex-1 h-2 bg-cream rounded-full overflow-hidden">
                    {/* Bar length inversely tracks pace: faster km = fuller bar. */}
                    <div className="h-full rounded-full" style={{ background: ROUTE_COLOR, width: `${Math.max(12, Math.min(100, 18000 / (s.paceSec || 600)))}%` }} />
                  </div>
                  <span className="w-16 text-right font-semibold text-charcoal tabular-nums">{fmtClock(s.paceSec)} {t.paceUnit}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {summary && (
          planId && sessionId ? (
            <p className={`text-xs font-inter text-center mt-3 ${saveState === 'error' ? 'text-red-600' : 'text-sage'}`}>
              {saveState === 'saving' ? t.savingRun
                : saveState === 'saved' ? `✓ ${saveKind === 'extra' ? t.savedExtra : t.savedMain}`
                : saveState === 'error' ? t.saveFailed : ''}
            </p>
          ) : (
            <p className="text-[11px] text-stone font-inter italic text-center mt-3">{t.saveNote}</p>
          )
        )}

        <div className="mt-6">
          {!tracking && !summary && (
            <Button onClick={start} disabled={!isNative}
              className="w-full h-14 rounded-full bg-primary text-lg font-manrope font-bold" data-testid="run-start">
              <Play className="w-5 h-5 mr-2" /> {t.start}
            </Button>
          )}
          {tracking && (
            <Button onClick={stop}
              className="w-full h-14 rounded-full bg-red-600 hover:bg-red-700 text-lg font-manrope font-bold" data-testid="run-stop">
              <Square className="w-5 h-5 mr-2" /> {t.stop}
            </Button>
          )}
          {!tracking && summary && (
            <Button onClick={reset} variant="outline"
              className="w-full h-12 rounded-full border-primary text-primary" data-testid="run-reset">
              {t.newRun}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
